import { CircleHelp } from 'lucide-react'
import { useEffect, useId, useLayoutEffect, useRef, useState, type PropsWithChildren } from 'react'
import { createPortal } from 'react-dom'

const BUBBLE_WIDTH = 240
const GUTTER = 8

/**
 * The bubble is only mounted while open and is portalled with fixed positioning
 * clamped to the viewport, so it never widens the page (the old always-mounted
 * absolute bubble caused horizontal scroll on phones) and works with tap as
 * well as hover/focus.
 */
export function InfoTip({ children }: PropsWithChildren) {
  const id = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const bubbleRef = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState(false)
  const [pinned, setPinned] = useState(false)
  const [position, setPosition] = useState<{ left: number; top: number; below: boolean } | null>(null)

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    const place = () => {
      const rect = triggerRef.current!.getBoundingClientRect()
      const vw = document.documentElement.clientWidth
      const width = Math.min(BUBBLE_WIDTH, vw - GUTTER * 2)
      const left = Math.min(Math.max(GUTTER, rect.left + rect.width / 2 - width / 2), vw - width - GUTTER)
      const height = bubbleRef.current?.offsetHeight ?? 60
      const below = rect.top - height - GUTTER < 0
      setPosition({ left, top: below ? rect.bottom + 6 : rect.top - height - 6, below })
    }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open])

  useEffect(() => {
    if (!pinned) return
    const close = (event: Event) => {
      if (triggerRef.current?.contains(event.target as Node)) return
      setPinned(false)
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setPinned(false); setOpen(false) }
    }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [pinned])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="info-tip"
        aria-label="说明"
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => { if (!pinned) setOpen(false) }}
        onFocus={() => setOpen(true)}
        onBlur={() => { if (!pinned) setOpen(false) }}
        onClick={(event) => {
          // Inside a <label>, a click would otherwise focus/toggle the associated control.
          event.preventDefault()
          event.stopPropagation()
          setPinned((value) => !value)
          setOpen(true)
        }}
      >
        <CircleHelp size={14} aria-hidden="true" />
      </button>
      {open && createPortal(
        <span
          ref={bubbleRef}
          id={id}
          role="tooltip"
          className="info-tip__bubble"
          style={{
            left: position?.left ?? -9999,
            top: position?.top ?? -9999,
            width: Math.min(BUBBLE_WIDTH, document.documentElement.clientWidth - GUTTER * 2),
          }}
        >
          {children}
        </span>,
        document.body,
      )}
    </>
  )
}
